<template>
  <div class="list-group flex-grow-1" style="max-width: 1400px;">
    <div
      v-if="!app || !isAuthAdmin"
      class="list-group-item flex-grow-1 py-5 text-center text-muted"
    >
      App not found. Please choose another.
    </div>

    <div v-else class="list-group-item d-flex flex-row">
      <h3 class="d-flex align-items-center m-0">
        <router-link
          class="btn btn-sm btn-light p-1"
          :to="app.environment === 'prod' ? '/' : '/all'"
        >
          <ArrowLeft style="stroke: #444;"></ArrowLeft>
        </router-link>
        <span class="mx-2">{{ app.title }}</span>
        <EnvBadge
          v-if="app.environment !== 'prod'"
          style="font-size: 1rem;"
          :env="app.environment"
          >{{ app.environment }}
        </EnvBadge>
        <small class="text-muted mx-2">Manage Access</small>
      </h3>

      <div v-if="isDeveloper" class="ml-auto">
        <AppSettingsDropdown
          :app="app"
          :allowed-roles="app.allowed_roles"
          @update-app="updateApp"
          @delete-app="deleteApp"
        />
      </div>
    </div>

    <div v-if="loading" class="list-group-item">
      <div class="text-center my-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>

    <div v-if="pendingUsers.length" class="list-group-item flex-grow-1 px-0">
      <h4 class="pl-5">
        Pending Users
      </h4>

      <UsersTablePending
        :users="pendingUsers"
        @update-user="updateUser"
        @delete-user="deleteUser"
      ></UsersTablePending>
    </div>

    <div v-if="authorizedUsers.length" class="list-group-item flex-grow-1 px-0">
      <h4 class="pl-5 mb-0">
        Authorized Users
      </h4>

      <UsersTableAuthorized
        :users="authorizedUsers"
        :allowed-roles="app.allowed_roles"
        :adding-user="addingUser"
        @update-user="updateUser"
        @delete-user="deleteUser"
        @add-user="addUser"
      ></UsersTableAuthorized>
    </div>
  </div>
</template>

<script>
import router from "../router"
import UsersTablePending from "../components/UsersTablePending.vue"
import UsersTableAuthorized from "../components/UsersTableAuthorized.vue"
import AppSettingsDropdown from "../components/AppSettingsDropdown.vue"
import ArrowLeft from "../assets/arrow-left.svg"
import EnvBadge from "../components/EnvBadge.vue"

export default {
  components: {
    UsersTableAuthorized,
    UsersTablePending,
    AppSettingsDropdown,
    EnvBadge,
    ArrowLeft,
  },
  props: {
    clientId: { required: true, type: String },
    apps: { required: true, type: Array },
    fetchApps: { required: true, type: Function },
  },
  data() {
    return {
      loading: true,
      addingUser: false,
      users: [],
    }
  },
  computed: {
    pendingUsers() {
      return this.users.filter((u) => u.roles.includes("requester"))
    },
    authorizedUsers() {
      return this.users.filter((u) => !u.roles.includes("requester"))
    },
    app() {
      return this.apps.find(({ cognito_id }) => cognito_id === this.clientId)
    },
    isAuthAdmin() {
      return this.app.user_roles.includes("auth_admin")
    },
    isDeveloper() {
      return this.app.user_roles.includes("developer")
    },
  },
  beforeMount() {
    this.fetchApp()
  },
  methods: {
    async updateUser(user) {
      const [_error, response] = await this.$api.put(
        `/apps/${this.clientId}/users/${user.emplid}`,
        user,
      )
      const index = this.users.findIndex((u) => u.emplid === user.emplid)
      this.$set(this.users, index, { ...user, ...response.data })
    },
    async deleteUser(user) {
      const doggoUser = await this.$auth.getUser()
      const doggoUserEmplid = doggoUser["custom:emplid"]
      const [_error, response] = await this.$api.delete(
        `/apps/${this.clientId}/users/${user.emplid}`,
      )
      if (response.status === 204) {
        const index = this.users.findIndex((u) => u.emplid === user.emplid)
        this.$delete(this.users, index)
        this.fetchApps()
        if (user.emplid === doggoUserEmplid) {
          router.push({ path: this.app.environment === "prod" ? "/" : "/all" })
        }
      }
    },
    async addUser(emplid) {
      const index = this.users.findIndex((u) => u.emplid === emplid)
      if (index !== -1) {
        this.$alert.fire({
          title: "User already added",
          icon: "warning",
        })
      } else {
        this.addingUser = true
        const [
          _error,
          response,
        ] = await this.$api.post(`/apps/${this.clientId}/users`, { emplid })
        if (response) {
          this.users = [...this.users, response.data]
          this.addingUser = false
          this.$alert.fire({
            title: "Success",
            text: `${response.data.cn} successfully added.`,
            icon: "success",
          })
        }
      }
    },
    async deleteApp(data) {
      const [_error, response] = await this.$api.delete(
        `/apps/${data.cognito_id}`,
      )
      if (response.status === 204) {
        console.log(response)
        this.$alert.fire({
          title: "Success",
          text: `${this.app.name} successfully deleted.`,
          icon: "success",
        })
        this.fetchApps()
        router.push({ path: "/" })
      }
    },
    async updateApp(data) {
      const [_error, response] = await this.$api.put(
        `/apps/${data.cognito_id}`,
        data,
      )
      if (response) {
        this.fetchApps()
      }
    },
    async fetchApp() {
      const [_error, response] = await this.$api.get(
        `/apps/${this.clientId}/manage`,
      )
      if (response) {
        this.users = response.data.users.map((u) => {
          return Object.fromEntries(
            Object.entries(u).map(([key, val]) => {
              return val.length > 0 ? [key, val] : [key, ""]
            }),
          )
        })
        this.loading = false
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.manage {
  display: flex;
  flex-grow: 1;
}
</style>
